import React, { Fragment, ReactNode } from 'react';

import Typography from '../../../../Typography/Typography';
import OptionBlock from '../../OptionBlock/OptionBlock';
import CheckboxIcon from '../../Checkbox/CheckboxIcon/CheckboxIcon';

export interface Props {
    /** Id of the field to be used with html For attribute. */
    id?: string;
    /** The post name of the radio. */
    name: string;
    /** The type of input, checkbox or radio. */
    type?: 'radio' | 'checkbox';
    /** Extra classes that can be added. */
    className?: string;
    /** Label text seen by the user. */
    label?: ReactNode;
    /** Optional metadata content - e.g. a sales tag or saving price - seen by the user. */
    metadata?: ReactNode;
    /** Text seen by the user that goes underneath the main label text. */
    subLabel?: ReactNode;
    /** Value of the radio option. */
    value?: string | number | undefined;
    /** Weather the radio is checked without state. */
    defaultChecked?: boolean | undefined;
    /** Whether the radio is checked. */
    checked?: boolean | number | undefined;
    /** Function to run on radio input change. */
    onChange?: null | ((e: React.ChangeEvent<HTMLInputElement>) => void);
    /** If the radio has an invalid value / error. */
    isValid?: boolean;
    /** required bool. */
    required?: number | boolean;
    /** If the radio should be disabled. */
    disabled?: boolean | undefined;
    readOnly?: boolean | undefined;
    text?: string | undefined;
    checkboxClasses?: string;
}

const RadioCheckboxBlock = ({
    id,
    name,
    type = 'radio',
    label = undefined,
    className = '',
    metadata = undefined,
    subLabel = undefined,
    defaultChecked = undefined,
    checked = undefined,
    onChange = null,
    isValid = true,
    required = 0,
    disabled = false,
    value = undefined,
    readOnly = undefined,
    text = undefined,
    checkboxClasses = '',
}: Props) => (
    <Fragment>
        <input
            id={id}
            name={name}
            className="hidden"
            type={type}
            value={value}
            defaultChecked={defaultChecked}
            checked={!!checked}
            onChange={onChange!}
            required={typeof required === 'number' ? !!+required : required}
            // @ts-expect-error
            disabled={disabled && 'disabled'}
            tabIndex={disabled ? -1 : 0}
            aria-invalid={!isValid}
            readOnly={readOnly}
        />
        <OptionBlock disabled={disabled} as="label" htmlFor={id} checked={!!checked} isDirty isValid={isValid} className={className}>
            {label ? <Typography as="div" typeset="emphasis" size="090">{label}</Typography> : null}
            {subLabel ? (
                <Typography as="div" typeset="paragraph" size="080">
                    {subLabel}
                </Typography>
            ) : null}
            <span className={`flex flex-row gap-x-050 ${checkboxClasses}`}>
                {type === 'checkbox' ? (
                    <React.Fragment>
                        <CheckboxIcon checked={!!checked} />
                        <Typography typeset="heading" size="090">
                            {text ?? label}
                        </Typography>
                    </React.Fragment>
                ) : null}
            </span>
            {metadata && metadata}
        </OptionBlock>
    </Fragment>
);

export default RadioCheckboxBlock;
